import { useState } from 'react';
import {
  Box, Typography, Grid, Paper, Chip, Stack, Button, IconButton,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import GitHubIcon from '@mui/icons-material/GitHub';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import portfolio from '../data/portfolio';

const categories = ['All', '3D Models', 'PCB', 'VTubing', 'Hacking'];

export default function Portfolio() {
  const [active, setActive] = useState('All');
  const filtered = active === 'All' ? portfolio : portfolio.filter((p) => p.category === active);

  return (
    <Box id="portfolio" sx={{ py: { xs: 10, md: 16 }, px: { xs: 3, md: 8 } }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Typography variant="overline" sx={{ color: 'primary.main', letterSpacing: 6, fontSize: '0.75rem' }}>
          03 — PORTFOLIO
        </Typography>
        <Typography variant="h2" sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' }, mb: 2, mt: 1 }}>
          My Work
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 6, maxWidth: 540 }}>
          A selection of projects spanning virtual avatars, custom hardware, merch & web design, and security research.
        </Typography>

        {/* Filter tabs */}
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 6, gap: 1 }}>
          {categories.map((cat) => (
            <Chip
              key={cat}
              label={cat}
              clickable
              onClick={() => setActive(cat)}
              sx={{
                fontWeight: 600,
                background: active === cat ? 'linear-gradient(135deg, #ff6eb4, #b44fff)' : 'transparent',
                border: active === cat ? 'none' : '1px solid rgba(255,110,180,0.3)',
                color: active === cat ? '#fff' : 'text.secondary',
                '&:hover': {
                  background: active === cat
                    ? 'linear-gradient(135deg, #ff6eb4, #b44fff)'
                    : 'rgba(255,110,180,0.1)',
                },
              }}
            />
          ))}
        </Stack>

        <Grid container spacing={3}>
          {filtered.map((project) => (
            <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={project.id}>
              <Paper
                sx={{
                  overflow: 'hidden',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  background: 'rgba(22,15,32,0.9)',
                  border: '1px solid rgba(255,110,180,0.12)',
                  transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    borderColor: project.color,
                    transform: 'translateY(-6px)',
                    boxShadow: `0 12px 40px ${project.color}22`,
                    '& .project-thumb': { transform: 'scale(1.05)' },
                  },
                }}
              >
                {/* Thumbnail */}
                <Box
                  className="project-thumb"
                  sx={{
                    background: `linear-gradient(135deg, ${project.color}22, ${project.color}08)`,
                    backgroundImage: `radial-gradient(${project.color}20 1px, transparent 1px)`,
                    backgroundSize: '20px 20px',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'transform 0.4s ease',
                    flexShrink: 0,
                  }}
                >
                  {project.coverImage ? (
                    <Box
                      component="img"
                      src={project.coverImage}
                      alt={project.title}
                      sx={{
                        display: 'block',
                        width: '100%',
                        height: 'auto',
                      }}
                    />
                  ) : (
                    <Typography sx={{
                      fontSize: '4rem', position: 'relative', zIndex: 1,
                      filter: `drop-shadow(0 0 20px ${project.color}88)`,
                    }}>
                      {project.emoji}
                    </Typography>
                  )}
                  <Chip
                    label={project.category}
                    size="small"
                    sx={{
                      position: 'absolute', top: 12, right: 12,
                      background: `${project.color}22`,
                      border: `1px solid ${project.color}55`,
                      color: project.color,
                      fontSize: '0.7rem',
                    }}
                  />
                </Box>

                <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                      {project.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1, whiteSpace: 'nowrap' }}>
                      {project.year}
                    </Typography>
                  </Box>

                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 2, flex: 1 }}>
                    {project.desc}
                  </Typography>

                  <Stack direction="row" spacing={0.8} flexWrap="wrap" sx={{ gap: 0.8, mb: 2 }}>
                    {project.tags.slice(0, 4).map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        sx={{
                          fontSize: '0.68rem',
                          background: 'rgba(255,110,180,0.07)',
                          border: '1px solid rgba(255,110,180,0.2)',
                          color: 'text.secondary',
                        }}
                      />
                    ))}
                  </Stack>

                  <Stack direction="row" spacing={1}>
                    <Button
                      component={RouterLink}
                      to={`/portfolio/${project.id}`}
                      size="small"
                      variant="outlined"
                      endIcon={<ArrowForwardIcon sx={{ fontSize: '14px !important' }} />}
                      sx={{
                        flex: 1,
                        borderColor: `${project.color}55`,
                        color: project.color,
                        fontSize: '0.75rem',
                        '&:hover': { borderColor: project.color, background: `${project.color}12` },
                      }}
                    >
                      Case Study
                    </Button>
                    {project.hasGithub && (
                      <IconButton
                        size="small"
                        sx={{
                          border: '1px solid rgba(255,110,180,0.2)',
                          color: 'text.secondary',
                          '&:hover': { color: 'primary.main', borderColor: 'primary.main' },
                        }}
                      >
                        <GitHubIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    )}
                  </Stack>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
}
